import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { getUser } from "../../api/auth";
import api from "../../api/axios";
import { useToast } from "../../context/ToastContext";

const MODULES = [
    "Dashboard",
    "Brands",
    "Templates",
    "Creatives",
    "Campaigns",
];

const createPermissions = () =>
    MODULES.map((module) => ({
        per_name: module,
        per_view: true,
        per_create: true,
        per_edit: true,
        per_delete: true,
    }));

export default function UserComponent({ role }) {
    const [search, setSearch] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({
        user_name: "",
        email: "",
        password: "",
        mobile: "",
        permissions: createPermissions(),
        status: "ACTIVE",
    });
    const [errors, setErrors] = useState({});
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [deleteUserId, setDeleteUserId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const { success, error } = useToast();

    const loadOrganizationUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get("/user/list?role=user");
            if (response?.data?.success) {
                setUsers(response.data.data.list || []);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.log(err?.response?.data?.message || err.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrganizationUsers();
    }, []);

    const filteredUsers = users.filter(
        (user) =>
            user.user_name.toLowerCase().includes(search.toLowerCase()) ||
            user.email.toLowerCase().includes(search.toLowerCase())
    );

    // Delete user (with API call)
    // const handleDelete = async (id) => {
    //     if (!window.confirm("Delete this user?")) return;
    //     try {
    //         await api.delete(`/user/${id}`);
    //         success("User deleted successfully");
    //         loadOrganizationUsers();
    //     } catch (err) {
    //         error(err?.response?.data?.message || "Failed to delete user");
    //     }
    // };


    const handleDelete = async () => {
        if (!deleteUserId) return;

        try {
            setDeleteLoading(true);
            await api.delete(`/user/${deleteUserId}`);
            success("User deleted successfully");
            setDeleteUserId(null);
            loadOrganizationUsers();
        } catch (err) {
            error(err?.response?.data?.message || "Failed to delete user");
        } finally {
            setDeleteLoading(false);
        }
    };

    // Input change handler – clears field error
    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        // Clear error for this field
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    // Edit handler
    const handleEdit = (user) => {
        setIsEdit(true);
        setEditId(user.id);
        setFormData({
            id: user.id,
            user_name: user.user_name,
            email: user.email,
            password: "", // password field hidden on edit
            mobile: user.mobile || "",
            status: user?.status || "ACTIVE",
            permissions: user.permissions
                ? [...user.permissions]
                : createPermissions(),
        });
        setErrors({});
        setOpenModal(true);
    };

    // Permission toggle
    const handlePermission = (index, field) => {
        const permissions = [...formData.permissions];
        permissions[index][field] = !permissions[index][field];
        setFormData({
            ...formData,
            permissions,
        });
    };

    // Reset form and errors
    const resetForm = () => {
        setFormData({
            user_name: "",
            email: "",
            password: "",
            mobile: "",
            permissions: createPermissions(),
            status: "",
        });
        setErrors({});
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};

        // User name: required, min 2 chars
        if (!formData.user_name || formData.user_name.trim().length < 2) {
            newErrors.user_name = "User name is required (minimum 2 characters)";
        }

        // Email: required, valid email
        if (!formData.email) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Password: required only when creating, min 6 chars
        if (!isEdit) {
            if (!formData.password) {
                newErrors.password = "Password is required";
            } else if (formData.password.length < 6) {
                newErrors.password = "Password must be at least 6 characters";
            }
        }

        // Mobile: optional, but if provided must be a valid 10-digit number (basic)
        if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
            newErrors.mobile = "Mobile number must be 10 digits";
        }

        // Status: required
        if (!formData.status) {
            newErrors.status = "Status is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save user (create or update)
    const handleSaveUser = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const userDetail = getUser();
            const payload = {
                ...formData,
                role: "user",
                org_name: userDetail?.user_name,
                org_id: userDetail?.org_id,
            };

            if (isEdit) {
                await api.put(`/user/${formData.id}`, payload);
                success("User updated successfully");
            } else {
                await api.post("/user/save", payload);
                success("User added successfully");
            }
            await loadOrganizationUsers();
            resetForm();
            setOpenModal(false);
        } catch (err) {
            console.log(err);
            error(
                err?.response?.data?.message ||
                err?.data?.message ||
                "An error occurred"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen py-0 sm:py-6 px-0 sm:px-6">
            <div className="mx-auto max-w-7xl">
                {/* Header */}
                <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        {/* <h1 className="text-3xl font-bold text-gray-800">Users</h1> */}
                        <h1 className="premium-page-title">
                            Users
                        </h1>
                        <p className="mt-1 text-gray-500">Manage users and permissions.</p>
                    </div>
                    <button
                        onClick={() => {
                            // resetForm();
                            setIsEdit(false);
                            setOpenModal(true);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 dashboard-btn-new px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        <Plus size={18} className="mr-1" />
                        Create User
                    </button>
                </div>

                {/* Search */}
                <div className="mb-6 rounded-xl bg-white p-5 shadow">
                    <input
                        type="text"
                        name="search"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        // className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-[#ff696866] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ff69681a]"

                    />
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-xl bg-white shadow">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Status</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user) => (
                                        <tr key={user.id} className="border-t hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium">{user.user_name}</td>
                                            <td className="px-6 py-4">{user.email}</td>
                                            <td className="px-6 py-4">{user.mobile}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${user.status === "ACTIVE"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"
                                                        }`}
                                                >
                                                    {user.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center gap-3">
                                                    <button
                                                        onClick={() => handleEdit(user)}
                                                        className="rounded-lg p-2 text-amber-600 transition hover:bg-amber-100"
                                                        title="Edit User"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    {/* <button
                                                        onClick={() => handleDelete(user.id)}
                                                        className="rounded-lg p-2 text-red-600 transition hover:bg-red-100"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button> */}
                                                    <button
                                                        onClick={() => setDeleteUserId(user.id)}
                                                        className="rounded-lg p-2 text-red-600 transition hover:bg-red-100"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="py-10 text-center text-gray-500">
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {openModal && (
                <div className="fixed inset-0 z-[111] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                        {/* Header */}
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {isEdit ? "Update User" : "Create User"}
                                </h2>
                            </div>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setOpenModal(false);
                                }}
                                className="text-3xl text-gray-400 hover:text-red-500"
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        {/* <div className="max-h-[75vh] overflow-y-auto p-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="mb-2 block font-medium">
                                        User Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="user_name"
                                        value={formData.user_name}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${errors.user_name ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Enter user name"
                                    />
                                    {errors.user_name && (
                                        <p className="mt-1 text-sm text-red-500">{errors.user_name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-2 block font-medium">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${errors.email ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Enter email"
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                                    )}
                                </div>

                                {!isEdit && (
                                    <div>
                                        <label className="mb-2 block font-medium">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInput}
                                            className={`w-full rounded-lg border px-4 py-3 ${errors.password ? "border-red-500" : "border-gray-300"
                                                }`}
                                            placeholder="Password (min 6 chars)"
                                        />
                                        {errors.password && (
                                            <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="mb-2 block font-medium">Mobile</label>
                                    <input
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${errors.mobile ? "border-red-500" : "border-gray-300"
                                            }`}
                                        placeholder="Mobile Number (10 digits)"
                                    />
                                    {errors.mobile && (
                                        <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 mb-2">
                                <h3 className="mb-5 text-xl font-semibold">Module Permissions</h3>
                                <div className="overflow-hidden rounded-xl border">
                                    <table className="w-full">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-5 py-2 text-left">Module</th>
                                                <th className="px-5 py-2 text-center">View</th>
                                                <th className="px-5 py-2 text-center">Create</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.permissions.map((item, index) => (
                                                <tr key={item.per_name} className="border-t hover:bg-gray-50">
                                                    <td className="px-5 py-3 font-medium">{item.per_name}</td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_view}
                                                            onChange={() => handlePermission(index, "per_view")}
                                                            className="h-4 w-4 accent-blue-600"
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_create}
                                                            onChange={() => handlePermission(index, "per_create")}
                                                            className="h-4 w-4 accent-blue-600"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInput}
                                    className={`w-full rounded-lg border px-4 py-3 ${errors.status ? "border-red-500" : "border-gray-300"
                                        } focus:border-blue-500 focus:outline-none`}
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Inactive</option>
                                </select>
                                {errors.status && (
                                    <p className="mt-1 text-sm text-red-500">{errors.status}</p>
                                )}
                            </div>
                        </div> */}

                        <div className="min-h-0 flex-1 overflow-y-auto p-6">
                            {/* User Details */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">
                                        User Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="user_name"
                                        value={formData.user_name}
                                        onChange={handleInput}
                                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 ${errors.user_name ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100" : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"}`}
                                        placeholder="Enter user name"
                                    />
                                    {errors.user_name && <p className="mt-1.5 text-sm text-red-500">{errors.user_name}</p>}
                                </div>

                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInput}
                                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 ${errors.email ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100" : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"}`}
                                        placeholder="Enter email"
                                    />
                                    {errors.email && <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                {!isEdit && (
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-slate-700">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInput}
                                            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 ${errors.password ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100" : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"}`}
                                            placeholder="Password (min 6 chars)"
                                        />
                                        {errors.password && <p className="mt-1.5 text-sm text-red-500">{errors.password}</p>}
                                    </div>
                                )}

                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">Mobile</label>
                                    <input
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInput}
                                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-200 ${errors.mobile ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100" : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"}`}
                                        placeholder="Mobile Number (10 digits)"
                                    />
                                    {errors.mobile && <p className="mt-1.5 text-sm text-red-500">{errors.mobile}</p>}
                                </div>
                            </div>

                            {/* Permissions */}
                            <div className="mt-8 mb-2">
                                <h3 className="mb-5 text-xl font-semibold text-slate-800">Module Permissions</h3>

                                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                    <table className="w-full">
                                        <thead className="bg-[#ff69680d]">
                                            <tr>
                                                <th className="px-5 py-3 text-left text-sm font-semibold text-slate-700">Module</th>
                                                <th className="px-5 py-3 text-center text-sm font-semibold text-slate-700">View</th>
                                                <th className="px-5 py-3 text-center text-sm font-semibold text-slate-700">Create</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {formData.permissions.map((item, index) => (
                                                <tr key={item.per_name} className="border-t border-slate-100 transition-colors hover:bg-[#ff69680d]">
                                                    <td className="px-5 py-3 text-sm font-medium text-slate-700">{item.per_name}</td>

                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_view}
                                                            onChange={() => handlePermission(index, "per_view")}
                                                            // className="h-4 w-4 cursor-pointer accent-[#ff6968]"
                                                            className="h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 bg-white outline-none transition-all duration-200 hover:border-[#ff6968] checked:border-[#ff6968] checked:bg-[#ff6968] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2024%2024%22%3E%3Cpath%20fill=%22none%22%20stroke=%22white%22%20stroke-width=%223%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%20d=%22M5%2012l4%204L19%208%22/%3E%3C/svg%3E')] checked:bg-[length:12px_12px] checked:bg-center checked:bg-no-repeat focus:outline-none focus:ring-0 focus:ring-offset-0"
                                                        />
                                                    </td>

                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_create}
                                                            onChange={() => handlePermission(index, "per_create")}
                                                            // className="h-4 w-4 cursor-pointer accent-[#ff6968]"
                                                            className="h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 bg-white outline-none transition-all duration-200 hover:border-[#ff6968] checked:border-[#ff6968] checked:bg-[#ff6968] checked:bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2024%2024%22%3E%3Cpath%20fill=%22none%22%20stroke=%22white%22%20stroke-width=%223%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%20d=%22M5%2012l4%204L19%208%22/%3E%3C/svg%3E')] checked:bg-[length:12px_12px] checked:bg-center checked:bg-no-repeat focus:outline-none focus:ring-0 focus:ring-offset-0"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Status */}
                            {/* <div className="mt-6">
                                <label className="mb-2 block text-sm font-medium text-slate-700">
                                    Status <span className="text-red-500">*</span>
                                </label>

                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInput}
                                    className={`w-full cursor-pointer rounded-xl border bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-all duration-200 ${errors.status ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100" : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"}`}
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Inactive</option>
                                </select>

                                {errors.status && <p className="mt-1.5 text-sm text-red-500">{errors.status}</p>}
                            </div> */}

                            <div className="mt-6">
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Status <span className="text-red-500">*</span>
                                </label>

                                <div className="relative">
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInput}
                                        className={`w-full appearance-none cursor-pointer rounded-xl border bg-white px-4 py-2.5 pr-10 text-sm text-slate-800 outline-none transition-all duration-200
                                                ${errors.status
                                                ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                                                : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"
                                            }`}
                                    >
                                        <option value="ACTIVE">Active</option>
                                        <option value="INACTIVE">Inactive</option>
                                    </select>

                                    {/* Dropdown Icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down 
                                                    absolute
                                                    right-3
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-gray-500
                                                    pointer-events-none" aria-hidden="true">
                                        <path d="m6 9 6 6 6-6"></path>
                                    </svg>
                                </div>
                                {errors.status && <p className="mt-1.5 text-sm text-red-500">{errors.status}</p>}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-3">
                            <button
                                onClick={() => {
                                    resetForm();
                                    setOpenModal(false);
                                }}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition-all duration-200 hover:border-[#ff696866] hover:bg-[#ff69680d] hover:text-[#e85b5a] focus:outline-none focus:ring-4 focus:ring-[#ff69681a]"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSaveUser}
                                disabled={submitting}
                                className="rounded-xl bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#f43f5e] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(255,105,104,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_9px_24px_rgba(255,105,104,0.28)] focus:outline-none focus:ring-4 focus:ring-[#ff69681a] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                            >
                                {submitting
                                    ? "Saving..."
                                    : isEdit
                                        ? "Update User"
                                        : "Create User"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteUserId && (
                <div className="fixed inset-0 z-[1111] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl animate-fadeIn">

                        {/* Header */}
                        <div className="px-6 pt-6">
                            <div className="flex items-start gap-4">

                                {/* Delete Icon */}
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50">
                                    <Trash2
                                        size={21}
                                        className="text-red-600"
                                    />
                                </div>

                                {/* Content */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Delete User
                                    </h3>

                                    <p className="mt-1.5 text-sm leading-5 text-gray-500">
                                        Are you sure you want to delete this user?
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="mt-6 border-t border-gray-100" />

                        {/* Actions */}
                        <div className="flex justify-end gap-3 bg-gray-50/70 px-6 py-4">

                            {/* Cancel */}
                            <button
                                type="button"
                                onClick={() => setDeleteUserId(null)}
                                disabled={deleteLoading}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Cancel
                            </button>

                            {/* Delete */}
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {deleteLoading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
};